import { z } from 'zod';

import { isHttpUrl } from './fields.js';
import { normalizeOnboardingSetup } from './onboarding-setup-normalization.js';
import { ClientContextErrorCodes, type ClientContextErrorCode } from './onboarding.js';

// ─── run_management.clients_params — customer setup ──────────────────
// Operational setup Data / Manus runs read. The client-context API is its only
// writer, once, when the customer is created; Nexus never writes it and never
// updates it. Onboarding reads use the Nexus setup snapshot in Postgres (below);
// clients_params stays a BigQuery read only for operational, non-onboarding
// readers. Brand / onboarding fields are never duplicated here.
//
// Keys are the literal BigQuery column names, so a form field can never be
// shifted onto a neighbouring column (Retool's New Customer INSERT writes
// its Google Account IDs input into `google_custom_event`; Nexus does not).

export type ClientsParamsValueType = 'string' | 'json' | 'bool' | 'date';

/**
 * The clients_params setup columns and their BigQuery types. Edit Organization
 * displays, read-only, only the form's setup fields saved in the Nexus
 * snapshot — not the display-only or create-constant columns.
 */
export const CLIENTS_PARAMS_SETUP_COLUMN_TYPES = {
    account_id: 'json',
    ts_start: 'date',
    tagging_sector: 'string',
    position_nc: 'bool',
    position_nc_sep: 'string',
    nc_names: 'json',
    nc_dict: 'json',
    overwrite_conversion: 'bool',
    overwrite_product: 'bool',
    custom_pixel: 'json',
    low_memory_version: 'bool',
    attribution_window: 'string',
    conversion_action: 'string',
    adlib_url: 'string',
    campaign_list: 'json',
    google_account_ids: 'json',
    google_custom_event: 'json',
    google_custom_conversion: 'json',
    front_product_column: 'string',
    nc_columns_level: 'json',
    is_active: 'bool',
    tiktok_advertiser_ids: 'json',
} as const satisfies Record<string, ClientsParamsValueType>;

export type ClientsParamsSetupColumn = keyof typeof CLIENTS_PARAMS_SETUP_COLUMN_TYPES;

export const CLIENTS_PARAMS_DISPLAY_COLUMNS = Object.keys(CLIENTS_PARAMS_SETUP_COLUMN_TYPES) as ClientsParamsSetupColumn[];

/** Stored in clients_params but not in the Nexus snapshot; New Customer has no source for them, so create never sends them. */
export const CLIENTS_PARAMS_DISPLAY_ONLY_COLUMNS = ['google_custom_conversion', 'tiktok_advertiser_ids'] as const;

/** Written on create regardless of the form, matching the Retool New Customer INSERT. */
export const CLIENTS_PARAMS_CREATE_CONSTANTS = {
    campaign_list: [] as string[],
    is_active: true,
} as const;

export const CLIENTS_PARAMS_TAGGING_SECTORS = ['ecommerce', 'gaming', 'test'] as const;
export type ClientsParamsTaggingSector = (typeof CLIENTS_PARAMS_TAGGING_SECTORS)[number];

/** "Platform Product Breakdown": Product Code → `Product_Code`, Product Type → `Product_Type`. */
export const CLIENTS_PARAMS_FRONT_PRODUCT_COLUMNS = ['Product_Code', 'Product_Type'] as const;
export type ClientsParamsFrontProductColumn = (typeof CLIENTS_PARAMS_FRONT_PRODUCT_COLUMNS)[number];

/** Retool's create-time fallbacks: a blank or cleared value resolves to these, never to NULL. */
export const CLIENTS_PARAMS_FALLBACKS = {
    position_nc_sep: '_',
    nc_names: [] as string[],
    nc_dict: {} as Record<string, unknown>,
    custom_pixel: [] as string[],
    attribution_window: '7d_click',
    conversion_action: 'omni_purchase',
    adlib_url: '',
    google_account_ids: [] as string[],
    google_custom_event: [] as string[],
    front_product_column: 'Product_Code' as ClientsParamsFrontProductColumn,
    nc_columns_level: [] as string[],
} as const;

export const CLIENTS_PARAMS_ACCOUNT_ID_PATTERN = /^act_\d+$/;

/** Size limits on the setup inputs, shared with the form so it flags the same values the server rejects. */
export const CLIENTS_PARAMS_LIMITS = {
    maxTextLength: 2_000,
    maxListItems: 200,
    maxListItemLength: 500,
    maxJsonLength: 50_000,
} as const;

const MAX_TEXT_LENGTH = CLIENTS_PARAMS_LIMITS.maxTextLength;
const MAX_LIST_ITEMS = CLIENTS_PARAMS_LIMITS.maxListItems;
const MAX_LIST_ITEM_LENGTH = CLIENTS_PARAMS_LIMITS.maxListItemLength;
const MAX_JSON_LENGTH = CLIENTS_PARAMS_LIMITS.maxJsonLength;
const MIN_TS_START_YEAR = 1000;
const MAX_TS_START_YEAR = 9999;
const WHITESPACE_PATTERN = /\s+/g;

// ─── Input schema ────────────────────────────────────────────────────

const listItemSchema = z.string().trim().min(1).max(MAX_LIST_ITEM_LENGTH);

function listWithFallback(fallback: readonly string[]) {
    return z
        .array(listItemSchema)
        .max(MAX_LIST_ITEMS)
        .nullish()
        .transform((value) => (value && value.length > 0 ? value : [...fallback]));
}

function textWithFallback(fallback: string) {
    return z
        .string()
        .max(MAX_TEXT_LENGTH)
        .nullish()
        .transform((value) => value?.trim() || fallback);
}

const checkboxSchema = z.boolean().default(false);

/** How far back a TS start may reach; it sets how far back data is pulled, so it is bounded. */
export const CLIENTS_PARAMS_TS_START_MAX_AGE_YEARS = 2;

/** The current month and the same month two years back, both inclusive, as `YYYY-MM`. Month level, never a day. */
export function tsStartMonthBounds(now: Date): { min: string; max: string } {
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    return {
        min: toMonthKey(year - CLIENTS_PARAMS_TS_START_MAX_AGE_YEARS, month),
        max: toMonthKey(year, month),
    };
}

function toMonthKey(year: number, month: number): string {
    return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}`;
}

/** Month + Year, stored as `DATE(year, month, 1)`. */
const tsStartSchema = z
    .object({
        year: z.number().int().min(MIN_TS_START_YEAR).max(MAX_TS_START_YEAR),
        month: z.number().int().min(1).max(12),
    })
    .strict();

const ncDictSchema = z
    .record(z.unknown())
    .nullish()
    .transform((value) => value ?? { ...CLIENTS_PARAMS_FALLBACKS.nc_dict })
    .refine((value) => JSON.stringify(value).length <= MAX_JSON_LENGTH, {
        message: `Must serialize to at most ${MAX_JSON_LENGTH} characters`,
    });

/**
 * The New Customer setup inputs. `google_custom_conversion`,
 * `tiktok_advertiser_ids`, `campaign_list` and `is_active` are deliberately
 * absent: the strict object rejects them.
 */
export const ClientsParamsSetupInputSchema = z
    .object({
        account_id: z
            .array(z.string().trim().regex(CLIENTS_PARAMS_ACCOUNT_ID_PATTERN, 'Must match act_<digits>'))
            .min(1, 'At least one account ID is required')
            .max(MAX_LIST_ITEMS),
        ts_start: tsStartSchema,
        tagging_sector: z.enum(CLIENTS_PARAMS_TAGGING_SECTORS),
        position_nc: checkboxSchema,
        position_nc_sep: textWithFallback(CLIENTS_PARAMS_FALLBACKS.position_nc_sep),
        nc_names: listWithFallback(CLIENTS_PARAMS_FALLBACKS.nc_names),
        nc_dict: ncDictSchema,
        overwrite_conversion: checkboxSchema,
        overwrite_product: checkboxSchema,
        custom_pixel: listWithFallback(CLIENTS_PARAMS_FALLBACKS.custom_pixel),
        low_memory_version: checkboxSchema,
        attribution_window: textWithFallback(CLIENTS_PARAMS_FALLBACKS.attribution_window),
        conversion_action: textWithFallback(CLIENTS_PARAMS_FALLBACKS.conversion_action),
        /** Retool: `adlibURLInput.value.replace(/\s+/g, '') || ""`. */
        adlib_url: z
            .string()
            .max(MAX_TEXT_LENGTH)
            .nullish()
            .transform((value) => (value ?? '').replace(WHITESPACE_PATTERN, ''))
            .refine((value) => value === '' || isHttpUrl(value), { message: 'Must be an http(s) URL' }),
        google_account_ids: listWithFallback(CLIENTS_PARAMS_FALLBACKS.google_account_ids),
        google_custom_event: listWithFallback(CLIENTS_PARAMS_FALLBACKS.google_custom_event),
        front_product_column: z
            .enum(CLIENTS_PARAMS_FRONT_PRODUCT_COLUMNS)
            .nullish()
            .transform((value) => value ?? CLIENTS_PARAMS_FALLBACKS.front_product_column),
        nc_columns_level: listWithFallback(CLIENTS_PARAMS_FALLBACKS.nc_columns_level),
    })
    .strict();

/** What a client sends: every fallback field may be omitted. */
export type ClientsParamsSetupInput = z.input<typeof ClientsParamsSetupInputSchema>;
/** Normalized setup, fallbacks applied. */
export type ClientsParamsSetupValues = z.output<typeof ClientsParamsSetupInputSchema>;

export type ClientsParamsSetupValidation =
    | { ok: true; values: ClientsParamsSetupValues }
    | { ok: false; code: ClientContextErrorCode; message: string };

/** `now` is injectable so the month range is testable; callers use the current date. */
export function validateClientsParamsSetup(input: unknown, now: Date = new Date()): ClientsParamsSetupValidation {
    const prepared = normalizeOnboardingSetup(input);
    if (!prepared.ok) {
        return {
            ok: false,
            code: ClientContextErrorCodes.VALIDATION_FAILED,
            message: `setup.${prepared.field}: ${prepared.reason}`,
        };
    }
    const parsed = ClientsParamsSetupInputSchema.safeParse(prepared.value);
    if (parsed.success) {
        const { year, month } = parsed.data.ts_start;
        const { min, max } = tsStartMonthBounds(now);
        const chosen = toMonthKey(year, month);
        if (chosen < min || chosen > max) {
            return {
                ok: false,
                code: ClientContextErrorCodes.VALIDATION_FAILED,
                message: `setup.ts_start: Must be within the last ${CLIENTS_PARAMS_TS_START_MAX_AGE_YEARS} years, not in the future`,
            };
        }
        const missingNamingField = parsed.data.position_nc ? missingNamingConventionField(prepared.value) : null;
        if (missingNamingField) {
            return {
                ok: false,
                code: ClientContextErrorCodes.VALIDATION_FAILED,
                message: `setup.${missingNamingField}: Required when position_nc is set`,
            };
        }
        return { ok: true, values: parsed.data };
    }

    const issue = parsed.error.issues[0];
    if (issue?.code === 'unrecognized_keys') {
        return {
            ok: false,
            code: ClientContextErrorCodes.UNKNOWN_FIELD,
            message: `Not a New Customer setup field: ${issue.keys.join(', ')}`,
        };
    }
    const path = issue?.path.length ? issue.path.join('.') : 'setup';
    return {
        ok: false,
        code: ClientContextErrorCodes.VALIDATION_FAILED,
        message: `setup.${path}: ${issue?.message ?? 'Invalid value'}`,
    };
}

/**
 * As in Retool, a naming convention needs its separator and part names. Checked on the raw
 * input, before the blank-means-fallback rule would fill in `_` or an empty list.
 */
function missingNamingConventionField(input: unknown): 'position_nc_sep' | 'nc_names' | null {
    const raw = (input ?? {}) as Record<string, unknown>;
    const separator = raw.position_nc_sep;
    if (typeof separator !== 'string' || separator.trim().length === 0) return 'position_nc_sep';
    const names = raw.nc_names;
    if (!Array.isArray(names) || !names.some((name) => typeof name === 'string' && name.trim().length > 0)) return 'nc_names';
    return null;
}

// ─── Create row ──────────────────────────────────────────────────────

export const CLIENTS_PARAMS_CREATE_COLUMNS = [
    'customer_name',
    'industry',
    'account_id',
    'ts_start',
    'tagging_sector',
    'position_nc',
    'position_nc_sep',
    'nc_names',
    'nc_dict',
    'overwrite_conversion',
    'overwrite_product',
    'custom_pixel',
    'low_memory_version',
    'attribution_window',
    'conversion_action',
    'adlib_url',
    'campaign_list',
    'google_account_ids',
    'google_custom_event',
    'front_product_column',
    'nc_columns_level',
    'is_active',
] as const;

export type ClientsParamsCreateColumn = (typeof CLIENTS_PARAMS_CREATE_COLUMNS)[number];

export type ClientsParamsCreateRow = Record<ClientsParamsCreateColumn, string | boolean | readonly string[] | Record<string, unknown>>;

/** The setup row New Customer sends to the client-context API: setup inputs + Client Industry + the Retool create constants. */
export function toClientsParamsCreateRow(
    customerName: string,
    industry: string,
    setup: ClientsParamsSetupValues,
): ClientsParamsCreateRow {
    return {
        customer_name: customerName,
        industry,
        ...setup,
        ts_start: toTsStartDate(setup.ts_start),
        ...CLIENTS_PARAMS_CREATE_CONSTANTS,
    };
}

/** `DATE(year, month, 1)` as `YYYY-MM-01`. */
export function toTsStartDate({ year, month }: { year: number; month: number }): string {
    return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-01`;
}

// ─── Nexus onboarding setup snapshot ─────────────────────────────────
// Nexus's copy of the setup a create submitted and the client-context API
// accepted (Postgres `tenant_onboarding_setups`) — the onboarding read source.
// Saved at create; afterwards only the Admin onboarding sync replaces it from clients_params. Exactly the
// form's setup fields: no industry (kept with client_context), no create
// constants, no display-only columns.

export type OnboardingSetupColumn = keyof ClientsParamsSetupValues;

export const ONBOARDING_SETUP_COLUMNS = Object.keys(ClientsParamsSetupInputSchema.shape) as OnboardingSetupColumn[];

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const snapshotList = z
    .array(z.string())
    .nullable()
    .transform((value) => value ?? []);
const snapshotText = z.string().nullable();
const snapshotBool = z.boolean().nullable();

/**
 * A stored snapshot. Scalars may be NULL so a legacy clients_params row can be
 * stored as-is without invented defaults; a NULL list is stored as empty.
 */
export const OnboardingSetupSnapshotSchema = z
    .object({
        account_id: snapshotList,
        ts_start: z.string().regex(ISO_DATE_PATTERN, 'Must be YYYY-MM-DD').nullable(),
        tagging_sector: snapshotText,
        position_nc: snapshotBool,
        position_nc_sep: snapshotText,
        nc_names: snapshotList,
        nc_dict: z.record(z.unknown()).nullable(),
        nc_columns_level: snapshotList,
        overwrite_conversion: snapshotBool,
        overwrite_product: snapshotBool,
        low_memory_version: snapshotBool,
        custom_pixel: snapshotList,
        attribution_window: snapshotText,
        conversion_action: snapshotText,
        adlib_url: snapshotText,
        google_account_ids: snapshotList,
        google_custom_event: snapshotList,
        front_product_column: snapshotText,
    })
    .strict() satisfies z.ZodType<unknown, z.ZodTypeDef, Record<OnboardingSetupColumn, unknown>>;

export type OnboardingSetupSnapshot = z.output<typeof OnboardingSetupSnapshotSchema>;

/** The accepted New Customer setup as Nexus stores it; `ts_start` becomes `YYYY-MM-01`. */
export function toOnboardingSetupSnapshot(setup: ClientsParamsSetupValues): OnboardingSetupSnapshot {
    return { ...setup, ts_start: toTsStartDate(setup.ts_start) };
}
